import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "#0a0f1e", color: "#f0f4ff", minHeight: "100vh", margin: 0, padding: 0, overflowX: "hidden" },

    // NAV
    nav: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: isMobile ? "16px 20px" : "20px 60px", borderBottom: "1px solid rgba(255,255,255,0.08)", position: "sticky", top: 0, background: "rgba(10,15,30,0.97)", backdropFilter: "blur(10px)", zIndex: 200 },
    navLogo: { fontSize: isMobile ? 20 : 24, fontWeight: 800, color: "#fff", letterSpacing: "-0.5px" },
    navLogoAccent: { color: "#3b82f6" },
    navLinks: { display: "flex", gap: 28, alignItems: "center" },
    navLink: { color: "rgba(255,255,255,0.7)", textDecoration: "none", fontSize: 14, fontWeight: 500, cursor: "pointer" },
    navCta: { background: "#3b82f6", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
    hamburger: { background: "transparent", border: "none", color: "#fff", fontSize: 24, cursor: "pointer", padding: 4 },

    // MOBILE MENU
    mobileMenu: { background: "rgba(10,15,30,0.98)", borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 16 },
    mobileLink: { color: "rgba(255,255,255,0.8)", fontSize: 16, fontWeight: 600, cursor: "pointer", padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" },
    mobileCta: { background: "#3b82f6", color: "#fff", border: "none", padding: "14px", borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: "pointer", marginTop: 4 },

    // HERO
    hero: { textAlign: "center", padding: isMobile ? "60px 20px 50px" : "100px 60px 80px", maxWidth: 900, margin: "0 auto" },
    heroBadge: { display: "inline-block", background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)", color: "#60a5fa", padding: "6px 16px", borderRadius: 999, fontSize: 12, fontWeight: 600, marginBottom: 24, letterSpacing: "0.05em" },
    heroTitle: { fontSize: isMobile ? 34 : 58, fontWeight: 800, lineHeight: 1.15, marginBottom: 20, letterSpacing: isMobile ? "-0.5px" : "-1px" },
    heroTitleAccent: { color: "#3b82f6" },
    heroSubtitle: { fontSize: isMobile ? 15 : 18, color: "rgba(255,255,255,0.6)", lineHeight: 1.7, maxWidth: 600, margin: "0 auto 36px" },
    heroButtons: { display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", flexDirection: isMobile ? "column" : "row", alignItems: "center" },
    heroPrimary: { background: "#3b82f6", color: "#fff", border: "none", padding: isMobile ? "16px 32px" : "16px 36px", borderRadius: 10, fontSize: isMobile ? 15 : 16, fontWeight: 700, cursor: "pointer", width: isMobile ? "100%" : "auto", maxWidth: isMobile ? 320 : "none" },
    heroSecondary: { background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", padding: isMobile ? "14px 32px" : "16px 36px", borderRadius: 10, fontSize: isMobile ? 15 : 16, fontWeight: 600, cursor: "pointer", width: isMobile ? "100%" : "auto", maxWidth: isMobile ? 320 : "none" },

    // STATS
    stats: { display: "flex", justifyContent: "center", gap: isMobile ? 20 : 60, padding: isMobile ? "40px 20px" : "60px", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", flexWrap: "wrap" },
    stat: { textAlign: "center", minWidth: isMobile ? "calc(50% - 20px)" : "auto" },
    statValue: { fontSize: isMobile ? 28 : 36, fontWeight: 800, color: "#3b82f6", marginBottom: 4 },
    statLabel: { fontSize: isMobile ? 12 : 14, color: "rgba(255,255,255,0.5)", fontWeight: 500 },

    // SECTIONS
    section: { padding: isMobile ? "50px 20px" : "80px 60px", maxWidth: 1100, margin: "0 auto" },
    sectionLabel: { color: "#3b82f6", fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 },
    sectionTitle: { fontSize: isMobile ? 26 : 38, fontWeight: 800, marginBottom: 14, letterSpacing: "-0.5px" },
    sectionSubtitle: { fontSize: isMobile ? 14 : 16, color: "rgba(255,255,255,0.55)", maxWidth: 520, lineHeight: 1.7, marginBottom: isMobile ? 36 : 56 },

    // STEPS
    steps: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 },
    step: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: isMobile ? 24 : 32 },
    stepNumber: { width: 40, height: 40, background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#3b82f6", marginBottom: 16 },
    stepTitle: { fontSize: 17, fontWeight: 700, marginBottom: 8 },
    stepDesc: { fontSize: 13, color: "rgba(255,255,255,0.55)", lineHeight: 1.7 },

    // FEATURES
    featuresBg: { background: "rgba(255,255,255,0.02)", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)" },
    features: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 },
    feature: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: isMobile ? 20 : 28 },
    featureIcon: { fontSize: 26, marginBottom: 14 },
    featureTitle: { fontSize: 16, fontWeight: 700, marginBottom: 8 },
    featureDesc: { fontSize: 13, color: "rgba(255,255,255,0.55)", lineHeight: 1.7 },

    // CTA
    cta: { textAlign: "center", padding: isMobile ? "60px 20px" : "100px 60px", maxWidth: 700, margin: "0 auto" },
    ctaTitle: { fontSize: isMobile ? 28 : 42, fontWeight: 800, marginBottom: 14, letterSpacing: "-0.5px" },
    ctaSubtitle: { fontSize: isMobile ? 14 : 16, color: "rgba(255,255,255,0.55)", marginBottom: 32, lineHeight: 1.7 },

    // FOOTER
    footer: { borderTop: "1px solid rgba(255,255,255,0.08)", padding: isMobile ? "28px 20px" : "40px 60px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, flexDirection: isMobile ? "column" : "row", textAlign: isMobile ? "center" : "left" },
    footerLogo: { fontSize: 18, fontWeight: 800, color: "#fff" },
    footerText: { fontSize: 12, color: "rgba(255,255,255,0.4)" },
  };

  return (
    <div style={s.root}>

      {/* NAV */}
      <nav style={s.nav}>
        <div style={s.navLogo}>
          i<span style={s.navLogoAccent}>Trust Secure</span>
        </div>
        {isMobile ? (
          <button style={s.hamburger} onClick={() => setMenuOpen(o => !o)}>
            {menuOpen ? "✕" : "☰"}
          </button>
        ) : (
          <div style={s.navLinks}>
            <span style={s.navLink} onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}>How it works</span>
            <span style={s.navLink} onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}>Features</span>
            <button style={s.navCta} onClick={() => navigate("/login")}>Get Started</button>
          </div>
        )}
      </nav>

      {/* MOBILE MENU */}
      {isMobile && menuOpen && (
        <div style={s.mobileMenu}>
          <div style={s.mobileLink} onClick={() => { setMenuOpen(false); document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" }); }}>How it works</div>
          <div style={s.mobileLink} onClick={() => { setMenuOpen(false); document.getElementById("features")?.scrollIntoView({ behavior: "smooth" }); }}>Features</div>
          <div style={s.mobileLink} onClick={() => { setMenuOpen(false); navigate("/login"); }}>Sign In</div>
          <button style={s.mobileCta} onClick={() => { setMenuOpen(false); navigate("/login"); }}>Get Started Free</button>
        </div>
      )}

      {/* HERO */}
      <div style={s.hero}>
        <div style={s.heroBadge}>🔒 Trusted Payment Marketplace</div>
        <h1 style={s.heroTitle}>
          Trade with confidence.<br />
          <span style={s.heroTitleAccent}>Powered by ITrust Secure.</span>
        </h1>
        <p style={s.heroSubtitle}>
          ITrust Secure connects buyers and sellers safely.
          Sellers get paid after delivery is confirmed — no more fraud, no more chargebacks.
        </p>
        <div style={s.heroButtons}>
          <button style={s.heroPrimary} onClick={() => navigate("/login")}>Start Trading Free</button>
          <button style={s.heroSecondary} onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}>See How It Works</button>
        </div>
      </div>

      {/* STATS */}
      <div style={s.stats}>
        {[
          { value: "100%", label: "Verified Transactions" },
          { value: "0", label: "Fraud Cases" },
          { value: "24/7", label: "Dispute Support" },
          { value: "$0", label: "To Get Started" },
        ].map((stat, i) => (
          <div key={i} style={s.stat}>
            <div style={s.statValue}>{stat.value}</div>
            <div style={s.statLabel}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* HOW IT WORKS */}
      <div id="how-it-works" style={s.section}>
        <div style={s.sectionLabel}>Process</div>
        <h2 style={s.sectionTitle}>How ITrust Secure works</h2>
        <p style={s.sectionSubtitle}>A simple three-step process that protects both buyers and sellers on every transaction.</p>
        <div style={s.steps}>
          {[
            { n: "1", title: "Agree on terms", desc: "Buyer and seller agree on the item, price, and delivery terms. Create a listing or submit an offer directly." },
            { n: "2", title: "Buyer completes payment", desc: "The buyer pays through ITrust Secure. The seller is notified and fulfills the order." },
            { n: "3", title: "Confirm and get paid", desc: "Once the buyer confirms receipt, the seller receives their payment directly. Disputes are handled fairly by our admin team." },
          ].map((step, i) => (
            <div key={i} style={s.step}>
              <div style={s.stepNumber}>{step.n}</div>
              <div style={s.stepTitle}>{step.title}</div>
              <p style={s.stepDesc}>{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FEATURES */}
      <div id="features" style={s.featuresBg}>
        <div style={s.section}>
          <div style={s.sectionLabel}>Features</div>
          <h2 style={s.sectionTitle}>Built for trust</h2>
          <p style={s.sectionSubtitle}>Every feature is designed to make buying and selling safe, fast, and transparent.</p>
          <div style={s.features}>
            {[
              { icon: "🔐", title: "Verified Payments", desc: "Sellers receive payment only after delivery is confirmed. Buyers and sellers both stay protected." },
              { icon: "⚖️", title: "Dispute Resolution", desc: "If something goes wrong, our admin team reviews the case and makes a fair decision for both parties." },
              { icon: "💬", title: "Price Negotiation", desc: "Chat directly with sellers. Propose prices, counter-offer, and agree on a deal before any money moves." },
              { icon: "📦", title: "Delivery Tracking", desc: "Sellers submit proof of delivery. Buyers confirm receipt. Every step is recorded and verified." },
              { icon: "🛍️", title: "Item Condition", desc: "Every listing shows item condition — New, Like New, Good, Fair, or Poor — so you always know what you're buying." },
              { icon: "🌍", title: "Buy & Sell", desc: "One account lets you buy and sell. List your items, receive offers, and manage everything in one place." },
            ].map((f, i) => (
              <div key={i} style={s.feature}>
                <div style={s.featureIcon}>{f.icon}</div>
                <div style={s.featureTitle}>{f.title}</div>
                <p style={s.featureDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={s.cta}>
        <h2 style={s.ctaTitle}>Ready to trade safely?</h2>
        <p style={s.ctaSubtitle}>Join ITrust Secure today — the marketplace where sellers get paid after delivery is confirmed and buyers never have to worry about fraud.</p>
        <button style={{ ...s.heroPrimary, width: isMobile ? "100%" : "auto", maxWidth: isMobile ? 320 : "none" }} onClick={() => navigate("/login")}>
          Create Free Account
        </button>
      </div>

      {/* FOOTER */}
      <footer style={s.footer}>
        <div style={s.footerLogo}>
          i<span style={{ color: "#3b82f6" }}>Trust Secure</span>
        </div>
        <div style={s.footerText}>
          © {new Date().getFullYear()} ITrust Secure LLC. All rights reserved. itrustsecure.com
        </div>
      </footer>
    </div>
  );
}